/*
"Mon compte" — self-service profile page. Calls GET /api/users/me and
shows the user identity (email, role), the latest successful login,
and the most recent automatic lockout. The lockout line is the
practical reason this page exists: a legitimate user who lands here
after a quiet period can spot whether their account was attacked
while they were away ("Last lockout: 2026-05-04 14:32 — if it wasn't
you, contact support"). lastLockedAt is never auto-cleared on the
server, so this signal survives any number of subsequent successful
logins.

A locked user cannot reach this page because they cannot log in;
that's why the page surfaces *historical* locks rather than the
current lockedUntil deadline (which is always in the past or null
once the user is logged in).

The page also exposes a self-service form for changing the email.
There is no password to change — authentication is passwordless via
emailed login codes. PATCH /api/users/me requires only the bearer
token; the short JWT TTL plus per-account lockout cover the
"stolen token" threat.
*/

import { useCallback, useEffect, useState, type FormEvent, type JSX } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { ApiError, api } from "../lib/api";
import { ErrorState } from "../components/ErrorState";
import { Skeleton } from "../components/Skeleton";

interface ProfileInfo
{
	id:				string;
	email:			string;
	role:			"USER" | "ADMIN";
	createdAt:		number;
	lastLoginAt:	number | null;
	lastLockedAt:	number | null;
}

export function ProfilePage(): JSX.Element
{
	const { t }		= useTranslation("profile");
	const { t: tc }	= useTranslation("common");

	const [fInfo,	fSetInfo]	= useState<ProfileInfo | null>(null);
	const [fError,	fSetError]	= useState<string | null>(null);

	const fLoad = useCallback(() =>
	{
		fSetError(null);
		api<ProfileInfo>("GET", "/users/me")
			.then(fSetInfo)
			.catch((vErr: unknown) =>
			{
				fSetError(vErr instanceof ApiError ? vErr.message : tc("error_network"));
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => { fLoad(); }, [fLoad]);

	if (fError !== null)
	{
		return <ErrorState message={fError} onRetry={fLoad} />;
	}

	if (fInfo === null)
	{
		return (
			<div className="card">
				<Skeleton variant="title" widthPercent={25} />
				<div style={{ height: "0.75rem" }} />
				<Skeleton variant="text"  widthPercent={40} />
				<Skeleton variant="text"  widthPercent={20} />
				<Skeleton variant="text"  widthPercent={45} />
				<Skeleton variant="text"  widthPercent={50} />
				<Skeleton variant="text"  widthPercent={35} />
			</div>
		);
	}

	return (
		<>
			<div className="card">
				<h2>{t("title")}</h2>

				<dl className="profile-list">
					<dt>{t("field_email")}</dt>
					<dd>{fInfo.email}</dd>

					<dt>{t("field_role")}</dt>
					<dd>
						<span className={`tag ${fInfo.role === "ADMIN" ? "tag--admin" : "tag--user"}`}>
							{fInfo.role}
						</span>
					</dd>

					<dt>{t("field_created_on")}</dt>
					<dd>{formatDateTime(fInfo.createdAt)}</dd>

					<dt>{t("field_last_login")}</dt>
					<dd>{fInfo.lastLoginAt !== null ? formatDateTime(fInfo.lastLoginAt) : <span className="muted">{tc("never")}</span>}</dd>

					<dt>{t("field_last_lockout")}</dt>
					<dd>
						{fInfo.lastLockedAt !== null
							? (
								<>
									<span className="tag tag--disabled">{formatDateTime(fInfo.lastLockedAt)}</span>
									<div className="muted" style={{ marginTop: "0.25rem" }}>
										{t("lockout_warning")}
									</div>
								</>
							)
							: <span className="muted">{tc("none")}</span>}
					</dd>
				</dl>
			</div>

			<EmailChangeForm currentEmail={fInfo.email} onUpdated={fSetInfo} />
		</>
	);
}

/*
Form to change the authenticated user's email. PATCH /api/users/me
requires only the bearer token; on success the parent profile state
is replaced with the fresh row so the displayed email updates inline.
*/
function EmailChangeForm(
	props: { currentEmail: string; onUpdated: (inInfo: ProfileInfo) => void }
): JSX.Element
{
	const { t }		= useTranslation("profile");
	const { t: tc }	= useTranslation("common");

	const [fEmail,	fSetEmail]	= useState<string>("");
	const [fError,	fSetError]	= useState<string | null>(null);
	const [fOk,		fSetOk]		= useState<boolean>(false);
	const [fBusy,	fSetBusy]	= useState<boolean>(false);

	const fHandleSubmit = async (inEvent: FormEvent<HTMLFormElement>) =>
	{
		inEvent.preventDefault();
		fSetError(null);
		fSetOk(false);

		if (fEmail.trim().toLowerCase() === props.currentEmail.toLowerCase())
		{
			fSetError(t("email_same_as_current"));
			return;
		}

		fSetBusy(true);

		try
		{
			const vUpdated = await api<ProfileInfo>(
				"PATCH",
				"/users/me",
				{ newEmail: fEmail.trim() }
			);

			props.onUpdated(vUpdated);
			fSetEmail("");
			fSetOk(true);
		}
		catch (vErr)
		{
			fSetError(translatePatchError(t, tc, vErr));
		}
		finally
		{
			fSetBusy(false);
		}
	};

	return (
		<form className="card" onSubmit={fHandleSubmit}>
			<h2>{t("email_form_title")}</h2>
			<div className="form-grid">
				<div>
					<label htmlFor="email-new">{t("new_email_label")}</label>
					<input
						id="email-new"
						type="email"
						autoComplete="email"
						value={fEmail}
						onChange={(e) => fSetEmail(e.target.value)}
						required
					/>
				</div>
				<div style={{ display: "flex", alignItems: "flex-end" }}>
					<button type="submit" className="button" disabled={fBusy}>
						{fBusy ? tc("updating") : tc("update")}
					</button>
				</div>
			</div>
			{fError !== null ? <div className="error">{fError}</div> : null}
			{fOk ? <div className="muted" style={{ color: "var(--color-success, #1b5e20)" }}>{t("email_updated")}</div> : null}
		</form>
	);
}

/*
Maps a PATCH /users/me ApiError to a localised user-facing message.
*/
function translatePatchError(
	inT:	TFunction,	// useTranslation("profile").t — accepts options bag
	inTc:	TFunction,
	inErr:	unknown
): string
{
	if (inErr instanceof ApiError)
	{
		switch (inErr.code)
		{
			case "email_taken":			return inT("err_email_taken");
			case "missing_field":		return inT("err_missing_field");
			case "unauthenticated":		return inT("err_unauthenticated");
			default:					return inT("err_default", { status: inErr.status });
		}
	}

	return inTc("error_network");
}

/*
Formats a ms-since-epoch timestamp to "YYYY-MM-DD HH:MM" in the local
timezone. Same shape as the formatter in CardsPage; duplicated here
to keep the page self-contained — a one-line helper isn't worth a
shared module.
*/
function formatDateTime(
	inMs:	number	// Timestamp in milliseconds
): string
{
	const vDate = new Date(inMs);	// Local Date
	const pad = (n: number) => String(n).padStart(2, "0");

	return `${vDate.getFullYear()}-${pad(vDate.getMonth() + 1)}-${pad(vDate.getDate())} ${pad(vDate.getHours())}:${pad(vDate.getMinutes())}`;
}
